/**
 * PORTAL CLIENTE — CALIDAD (FASE 7.3)
 *
 *   /portal/calidad                          No conformidades (por defecto) · Incidencias
 *   /portal/calidad/no-conformidades/nueva   F11.3: abrir una NC (centro, ubicación, percepción, evidencia)
 *   /portal/calidad/incidencias/:id          Qué pasó, dónde, en qué estado, qué se hizo, evidencias
 *   /portal/calidad/no-conformidades/:id     F11.3: seguimiento, conversación, evidencias, acciones compartidas, reabrir
 *
 * Pantallas DISTINTAS de las de gestión (`paginas/calidad.tsx`), sólo con el
 * DTO público (`/api/portal/calidad/*`). Aquí no hay — ni puede haber, porque
 * el servidor no lo envía — responsable, análisis de causa, acciones o
 * comentarios internos, autores internos, fecha de compromiso ni evidencias
 * internas. El cliente colabora (abre, comenta, aporta evidencias, reabre);
 * la gravedad oficial la clasifica la empresa.
 */
import { useId, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  type AccionPublica,
  type ComentarioPublico,
  type HitoPublico,
  type IncidenciaPublicaDetalle,
  type IncidenciaPublicaFila,
  type IndicadoresCalidadPublicos,
  type NcPublicaDetalle,
  type NcPublicaFila,
  type ResultadoCrearNc,
  type ResumenServicioPortal,
} from '../api.ts';
import { Cargando, Error as ErrorCaja, EstadoNc, fecha, fechaHora, useDatos, Vacio } from '../componentes/base.tsx';
import { EstadoAccionEtiqueta, EstadoIncidenciaEtiqueta, NivelEtiqueta, textoPercepcionCliente } from '../componentes/calidad.tsx';
import { ReabrirNc } from '../componentes/no-conformidades.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';
import { SelectorPeriodo } from '../componentes/selector-periodo.tsx';
import { BotonExportarExcel } from '../componentes/exportar-excel.tsx';
import { ExportarInformeCalidad } from '../componentes/exportar-informe-calidad.tsx';
import { errorDelRango, esClavePeriodo, MAX_DIAS_ANALITICA, rangoDe } from '../periodo-analitico.ts';
import { ListaEvidenciasPublicas } from '../componentes/evidencias.tsx';
import { IND, useIndicadoresPortal } from '../componentes/indicadores-portal.ts';
import { Shell } from '../componentes/shell.tsx';
import { BloqueEvolucion } from './portal-evolucion.tsx';
import { textoPorcentaje, useCentrosPortal } from './portal-inicio.tsx';

type Pestana = 'incidencias' | 'nc' | 'indicadores';

const ETIQUETA_ORIGEN: Record<string, string> = { INTERNA: 'Detectada por el servicio', CLIENTE: 'Abierta por tu organización', AUDITORIA: 'Auditoría' };

function Dato({ e, children }: { e: string; children: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{children}</div>
    </div>
  );
}

/** Si el cliente ve la OT, se enlaza a SU ficha de portal; si no hay OT, se dice «del centro». */
function ContextoOt({ ot }: { ot: { id: string; codigo: string } | null }): ReactNode {
  return ot === null ? <span>Del centro (sin OT)</span> : <Link to={`/mis-ots/${ot.id}`}>{ot.codigo}</Link>;
}

function Evolucion({ hitos }: { hitos: readonly HitoPublico[] }): ReactNode {
  if (hitos.length === 0) return <p className="pequena tenue">Sin novedades todavía.</p>;
  return (
    <ol className="lista-nc-historial" aria-label="Evolución">
      {hitos.map((h, k) => (
        <li key={k}>
          <span className="pequena">{h.texto}</span>
          <span className="pequena tenue"> · {fechaHora(h.fecha)}</span>
          {h.detalle !== undefined && <div className="pequena suave">Motivo: {h.detalle}</div>}
        </li>
      ))}
    </ol>
  );
}

function useCentros(): { id: string; nombre: string }[] {
  return useDatos(() => api.get<{ filas: { id: string; nombre: string }[] }>('/api/portal/calidad/centros'), []).datos?.filas ?? [];
}

// ------------------------------------------------------------------ inicio

export function PaginaPortalCalidad(): ReactNode {
  const [params, setParams] = useSearchParams();
  //  F11.3: Calidad abre en sus no conformidades (antes abría en Incidencias aunque estuviera vacía).
  const pestana: Pestana = params.get('tab') === 'incidencias' ? 'incidencias' : params.get('tab') === 'indicadores' ? 'indicadores' : 'nc';
  const resumen = useDatos(() => api.get<{ incidenciasAbiertas: number; ncAbiertas: number }>('/api/portal/calidad/resumen'), []);
  return (
    <Shell
      titulo="Calidad"
      subtitulo={
        resumen.datos === null ? undefined : (
          <span className="pequena">
            {resumen.datos.incidenciasAbiertas} incidencia(s) abierta(s) · {resumen.datos.ncAbiertas} no conformidad(es) abierta(s)
          </span>
        )
      }
      tabs={
        <nav className="tabs" aria-label="Secciones de calidad" role="tablist">
          {([['incidencias', 'Incidencias'], ['nc', 'No conformidades'], ['indicadores', 'Indicadores']] as const).map(([clave, texto]) => (
            <a
              key={clave}
              href={`?tab=${clave}`}
              role="tab"
              aria-selected={pestana === clave}
              className={pestana === clave ? 'activo' : ''}
              onClick={(ev) => { ev.preventDefault(); setParams({ tab: clave }); }}
            >
              {texto}
            </a>
          ))}
        </nav>
      }
    >
      {pestana === 'incidencias' ? <ListaIncidencias /> : pestana === 'indicadores' ? <IndicadoresPublicos /> : <ListaNc />}
    </Shell>
  );
}

function ListaIncidencias(): ReactNode {
  const idBase = useId();
  const centros = useCentros();
  const [estado, setEstado] = useState('');
  const [idCentro, setIdCentro] = useState('');
  const [nivel, setNivel] = useState('');
  const [conNc, setConNc] = useState('');
  const lista = useDatos(() => {
    const q = new URLSearchParams();
    if (estado !== '') q.set('estado', estado);
    if (idCentro !== '') q.set('idCentro', idCentro);
    if (nivel !== '') q.set('nivel', nivel);
    if (conNc !== '') q.set('conNc', conNc);
    return api.get<{ filas: IncidenciaPublicaFila[] }>(`/api/portal/calidad/incidencias?${q.toString()}`);
  }, [estado, idCentro, nivel, conNc]);
  return (
    <>
    <ExportarInformeCalidad tipo="INCIDENCIAS" perspectiva="PORTAL" idCentro={idCentro} estado={estado} gravedad={nivel} noAplicados={conNc !== '' ? ['no conformidad'] : []} />
    <div className="panel">
      <div className="panel-cuerpo">
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-e`}>Estado</label>
            <select id={`${idBase}-e`} value={estado} onChange={(ev) => setEstado(ev.target.value)}>
              <option value="">Todos</option>
              <option value="ABIERTAS">Pendientes</option>
              <option value="RESUELTA">Resuelta</option>
              <option value="CERRADA">Cerrada</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-c`}>Centro</label>
            <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => setIdCentro(ev.target.value)}>
              <option value="">Todos</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-n`}>Nivel</label>
            <select id={`${idBase}-n`} value={nivel} onChange={(ev) => setNivel(ev.target.value)}>
              <option value="">Todos</option>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-v`}>No conformidad</label>
            <select id={`${idBase}-v`} value={conNc} onChange={(ev) => setConNc(ev.target.value)}>
              <option value="">Todas</option>
              <option value="si">Con no conformidad</option>
              <option value="no">Sin no conformidad</option>
            </select>
          </div>
        </div>
        {lista.cargando && lista.datos === null && <Cargando texto="Cargando incidencias…" />}
        {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
        {lista.datos !== null &&
          (lista.datos.filas.length === 0 ? (
            <Vacio titulo="No tienes incidencias visibles actualmente." />
          ) : (
            <ul className="lista-nc">
              {lista.datos.filas.map((f) => (
                <li key={f.id} className="nc-tarjeta">
                  <Link to={`/portal/calidad/incidencias/${f.id}`} className="nc-tarjeta-cabecera" style={{ textDecoration: 'none' }}>
                    <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="mono">{f.codigo}</span>
                      <EstadoIncidenciaEtiqueta estado={f.estado} />
                      <NivelEtiqueta nivel={f.nivel} />
                    </span>
                    <span className="pequena suave">{fecha(f.creadaEn.slice(0, 10))}</span>
                  </Link>
                  <div className="nc-tarjeta-resumen">
                    <p>{f.titulo}</p>
                    <p className="pequena tenue">
                      {[f.centro.nombre, f.ubicacion ?? null, f.ot === null ? 'Del centro' : f.ot.codigo].filter((x) => x !== null && x !== '').join(' · ')}
                      {f.resueltaEn !== null && ` · Resuelta el ${fecha(f.resueltaEn.slice(0, 10))}`}
                      {f.nc !== null && ` · ${f.nc.codigo}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ))}
      </div>
    </div>
    </>
  );
}

function ListaNc(): ReactNode {
  const idBase = useId();
  const { puede } = useSesion();
  const centros = useCentros();
  const ubicacion = useLocation();
  //  F11.3: filtros en la URL — al volver de una ficha, la lista está como se dejó.
  //  F11.4: desde los indicadores llega también la ubicación (con su nombre para el aviso) y el periodo de alta.
  const lista = useListaUrl({ tab: 'nc', estado: 'ABIERTAS', idCentro: '', idUbicacion: '', ubicacion: '', desde: '', hasta: '' });
  const estado = lista.valor('estado');
  const idCentro = lista.valor('idCentro');
  const idUbicacion = lista.valor('idUbicacion');
  const desde = lista.valor('desde');
  const hasta = lista.valor('hasta');
  const datos = useDatos(() => {
    const q = new URLSearchParams();
    if (estado !== 'TODAS') q.set('estado', estado);
    if (idCentro !== '') q.set('idCentro', idCentro);
    if (idUbicacion !== '') q.set('idUbicacion', idUbicacion);
    if (desde !== '') q.set('desde', desde);
    if (hasta !== '') q.set('hasta', hasta);
    return api.get<{ filas: NcPublicaFila[] }>(`/api/portal/calidad/no-conformidades?${q.toString()}`);
  }, [estado, idCentro, idUbicacion, desde, hasta]);
  const volver = { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Calidad' };
  return (
    <>
    <ExportarInformeCalidad
      tipo="NO_CONFORMIDADES" perspectiva="PORTAL" idCentro={idCentro} estado={estado} gravedad=""
      desdeVista={desde} hastaVista={hasta} noAplicados={idUbicacion !== '' ? ['ubicación'] : []}
    />
    <div className="panel">
      <div className="panel-cabecera">
        <h2>No conformidades</h2>
        {puede('nc:crear') && (
          <Link to="/portal/calidad/no-conformidades/nueva" state={{ volver }} className="boton principal boton-tactil">
            Nueva no conformidad
          </Link>
        )}
      </div>
      <div className="panel-cuerpo">
        {(idUbicacion !== '' || desde !== '' || hasta !== '') && (
          <div className="fila chips-drill" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10 }} role="group" aria-label="Filtros desde Indicadores">
            {idUbicacion !== '' && (
              <button type="button" className="chip-filtro" onClick={() => lista.fijarVarios({ idUbicacion: '', ubicacion: '' })}
                aria-label={`Quitar filtro: ubicación ${idUbicacion === 'SIN' ? 'sin ubicación' : lista.valor('ubicacion')}`}>
                Ubicación: {idUbicacion === 'SIN' ? 'sin ubicación' : lista.valor('ubicacion') || 'elegida'} <span aria-hidden="true">✕</span>
              </button>
            )}
            {(desde !== '' || hasta !== '') && (
              <button type="button" className="chip-filtro" onClick={() => lista.fijarVarios({ desde: '', hasta: '' })} aria-label="Quitar filtro de periodo">
                Abiertas del {desde === '' ? '…' : fecha(desde)} al {hasta === '' ? '…' : fecha(hasta)} <span aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        )}
        <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-e`}>Estado</label>
            <select id={`${idBase}-e`} value={estado} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
              <option value="ABIERTAS">Activas</option>
              <option value="CERRADA">Cerradas</option>
              <option value="TODAS">Todas</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-c`}>Centro</label>
            <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => lista.fijar('idCentro', ev.target.value)}>
              <option value="">Todos</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        </div>
        {datos.cargando && datos.datos === null && <Cargando texto="Cargando no conformidades…" />}
        {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
        {datos.datos !== null &&
          (datos.datos.filas.length === 0 ? (
            <Vacio titulo={estado === 'ABIERTAS' ? 'No tienes no conformidades activas.' : 'No hay no conformidades con estos filtros.'} />
          ) : (
            <ul className="lista-nc">
              {datos.datos.filas.map((n) => <TarjetaNcPublica key={n.id} n={n} volver={volver} />)}
            </ul>
          ))}
      </div>
    </div>
    </>
  );
}

/** Duración legible: «menos de 1 h», «5,5 h», «2,5 días». */
function textoHoras(horas: number | null): string {
  if (horas === null) return '—';
  if (horas < 1) return 'menos de 1 h';
  return horas >= 48 ? `${(horas / 24).toFixed(1).replace('.', ',').replace(/,0$/, '')} días` : `${horas.toFixed(1).replace('.', ',').replace(/,0$/, '')} h`;
}

function Cifras({ etiqueta, cifras }: { etiqueta: string; cifras: readonly (readonly [string, string, string | undefined])[] }): ReactNode {
  return (
    <div className="fila kpi-rejilla" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12 }} role="group" aria-label={etiqueta}>
      {cifras.map(([t, v, det]) => (
        <div key={t} className="panel kpi-tarjeta" style={{ flex: '1 1 150px', margin: 0 }}>
          <div className="panel-cuerpo">
            <div className="etiqueta-dato">{t}</div>
            <div className="cifra" style={{ fontSize: '1.6rem', fontWeight: 600 }}>{v}</div>
            {det !== undefined && <div className="pequena tenue">{det}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Portal cliente v2 — la actividad de limpieza del periodo, con el MISMO cálculo que Inicio (`/api/portal/servicio`):
 * planificadas, realizadas, verificadas, tareas No OK, no realizadas, cumplimiento y verificación.
 */
function ActividadLimpieza({ desde, hasta, idCentro }: { desde: string; hasta: string; idCentro: string }): ReactNode {
  const q = new URLSearchParams({ desde, hasta });
  if (idCentro !== '') q.set('idCentro', idCentro);
  const d = useDatos(() => api.get<ResumenServicioPortal>(`/api/portal/servicio?${q.toString()}`), [q.toString()]);
  const r = d.datos !== null && !d.cargando && d.datos.periodo.desde === desde && d.datos.periodo.hasta === hasta && (d.datos.idCentro ?? '') === idCentro ? d.datos : null;
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>Actividad de limpieza</h2></div>
      <div className="panel-cuerpo">
        {d.error !== null && <ErrorCaja error={d.error} reintentar={d.recargar} />}
        {r === null && d.error === null && <Cargando texto="Calculando la actividad…" />}
        {r !== null && (
          <Cifras etiqueta="Actividad de limpieza" cifras={[
            ['Planificadas', String(r.ot.previstas), 'órdenes de trabajo'],
            ['Realizadas', String(r.ot.realizadas), `${String(r.tareas.realizadas)} de ${String(r.tareas.total)} tareas`],
            ['Verificadas', String(r.ot.verificadas), `${String(r.tareas.verificadas)} tareas`],
            ['Tareas No OK', String(r.tareas.noOk), 'resultado no aceptado'],
            ['No realizadas', String(r.ot.noRealizadas), r.ot.vencidas > 0 ? `y ${String(r.ot.vencidas)} fuera de plazo` : undefined],
            ['Cumplimiento', textoPorcentaje(r.cumplimiento), r.cumplimiento.pct === null ? 'nada debía estar hecho aún' : `${String(r.cumplimiento.n)} de ${String(r.cumplimiento.total)}`],
            ['Verificación', textoPorcentaje(r.verificacion), r.verificacion.pct === null ? 'sin limpiezas realizadas' : `${String(r.verificacion.n)} de ${String(r.verificacion.total)}`],
          ]} />
        )}
      </div>
    </div>
  );
}

/**
 * F11.4 — indicadores PÚBLICOS: sólo lo que el cliente puede ver de sus propias NC. Nada interno
 * (responsables, plazos internos, acciones internas, clasificación pendiente como fallo). Cada
 * ubicación lleva a sus NC.
 *
 * Portal cliente v2: filtro de centro (sólo los SUYOS: el servidor rechaza cualquier otro), la actividad de
 * limpieza del periodo, sus incidencias visibles, y las NC activas separadas en abiertas / en tratamiento.
 *
 * PC-v2.2-B: cada cifra y cada bloque se enseñan si la configuración del cliente (o del centro elegido) lo deja
 * (`GET /api/portal/indicadores`). Un bloque sin nada visible no se pinta (ni su título); si no queda ninguno, un
 * aviso en su lugar. Qué NC e incidencias cuentan no cambia: lo sigue decidiendo el servidor.
 *
 * PC-v2.2-C2.1: el periodo es el común de la analítica (`periodo-analitico.ts`), con «Personalizado» de hasta 366
 * días (el límite del servidor para la actividad y la evolución). Un periodo inválido no se pide.
 */
function IndicadoresPublicos(): ReactNode {
  const idBase = useId();
  const lista = useListaUrl({ tab: 'indicadores', periodo: '90', desde: '', hasta: '', idCentro: '', evolucion: '' });
  const periodoUrl = lista.valor('periodo');
  const periodo = esClavePeriodo(periodoUrl) ? periodoUrl : '90';
  const idCentro = lista.valor('idCentro');
  const centros = useCentrosPortal();
  const rango = rangoDe(periodo, lista.valor('desde'), lista.valor('hasta'));
  const { desde, hasta } = rango;
  const errorPeriodo = errorDelRango(rango, MAX_DIAS_ANALITICA);
  const consulta = new URLSearchParams({ desde, hasta });
  if (idCentro !== '') consulta.set('idCentro', idCentro);
  const d = useDatos(
    () => (errorPeriodo === null ? api.get<IndicadoresCalidadPublicos>(`/api/portal/calidad/indicadores?${consulta.toString()}`) : Promise.resolve(null)),
    [consulta.toString(), errorPeriodo],
  );
  const enlace = (u: IndicadoresCalidadPublicos['porUbicacion'][number]): string => {
    const q = new URLSearchParams({ tab: 'nc', estado: 'TODAS', idCentro: u.idCentro, idUbicacion: u.idUbicacion ?? 'SIN', ubicacion: u.ubicacion, desde, hasta });
    return `/portal/calidad?${q.toString()}`;
  };
  const indicadores = useIndicadoresPortal(idCentro);
  const v = indicadores.visibles;
  //  Hasta tener la configuración de ESTE centro no se pinta nada de lo configurable.
  //  Ni los datos del centro o del periodo anterior mientras llegan los del nuevo.
  const x = v === null || d.datos === null || d.cargando || (d.datos.idCentro ?? '') !== idCentro ? null : d.datos;
  const horas = x?.tiempoMedioResolucion.horas ?? null;
  const ve = (id: string): boolean => v?.has(id) === true;
  const cifrasIncidencias = x?.incidencias === undefined || x === null ? [] : ([
    [IND.calidad.incAbiertas, 'Abiertas ahora', String(x.incidencias.abiertas), undefined],
    [IND.calidad.incEnAnalisis, 'En análisis ahora', String(x.incidencias.enAnalisis), 'el equipo de servicio las está tratando'],
    [IND.calidad.incRegistradas, 'Registradas en el periodo', String(x.incidencias.registradas), undefined],
    [IND.calidad.incResueltas, 'Resueltas en el periodo', String(x.incidencias.resueltas), undefined],
    [IND.calidad.incTiempoMedio, 'Tiempo medio de resolución', textoHoras(x.incidencias.tiempoMedioResolucion.horas),
      x.incidencias.tiempoMedioResolucion.n === 0 ? 'Sin resoluciones en el periodo' : `del registro a la resolución · ${String(x.incidencias.tiempoMedioResolucion.n)} resuelta(s)`],
  ] as const).filter(([id]) => ve(id)).map(([, ...c]) => c);
  const cifrasNc = x === null ? [] : ([
    [IND.calidad.ncActivas, 'Activas ahora', String(x.activas),
      x.abiertas !== undefined && x.enTratamiento !== undefined ? `${String(x.abiertas)} abierta(s) · ${String(x.enTratamiento)} en tratamiento` : undefined],
    [IND.calidad.ncRegistradas, 'Abiertas en el periodo', String(x.registradas), undefined],
    [IND.calidad.ncCerradas, 'Cerradas en el periodo', String(x.cerradas), undefined],
    [IND.calidad.ncTiempoMedio, 'Tiempo medio de resolución', textoHoras(horas),
      x.tiempoMedioResolucion.n === 0 ? 'Sin cierres en el periodo' : `de la apertura al cierre · ${String(x.tiempoMedioResolucion.n)} cerrada(s)`],
    [IND.calidad.ncReaperturas, 'Reaperturas', String(x.reaperturas), 'en el periodo'],
  ] as const).filter(([id]) => ve(id)).map(([, ...c]) => c);
  //  PC-v2.2-C1: la evolución de NC (antes una tabla aquí) es ahora un gráfico del bloque «Evolución».
  const fila3 = [ve(IND.calidad.ncGravedad), ve(IND.calidad.ncPorCentro)];
  //  Sólo con la configuración (no con los datos, que pueden estar llegando). Lo que exige permisos ya viene recortado.
  const nada = v !== null && !Object.values(IND.calidad).some((id) => v.has(id));
  return (
    <>
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="portal-filtros">
            {centros.length > 1 && (
              <div className="campo filtros-calidad" style={{ margin: 0, maxWidth: 320 }}>
                <label htmlFor={`${idBase}-c`}>Centro</label>
                <select id={`${idBase}-c`} value={idCentro} onChange={(ev) => lista.fijar('idCentro', ev.target.value)}>
                  <option value="">Todos tus centros</option>
                  {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            )}
            <SelectorPeriodo idBase={idBase} periodo={periodo} rango={rango} error={errorPeriodo} fijar={(c) => lista.fijarVarios(c)} estilo="portal" />
            {/* PC-v2.2-C2.2: el Excel de lo que se ve aquí, con este centro y este periodo (lo recorta el servidor). */}
            <BotonExportarExcel
              ruta={errorPeriodo === null ? `/api/portal/calidad/exportar?${consulta.toString()}` : null}
              motivoDeshabilitado={errorPeriodo !== null ? 'Corrige el periodo para exportar.' : v === null ? 'Cargando los indicadores…' : nada ? 'No hay indicadores que exportar.' : null}
            />
          </div>
          {errorPeriodo === null && (
            <p className="pequena tenue" style={{ marginBottom: 0 }}>
              Del {fecha(desde)} al {fecha(hasta)}. «Activas» es la situación de ahora; el resto, lo ocurrido en el periodo.
            </p>
          )}
        </div>
      </div>
      {indicadores.error !== null && <ErrorCaja error={indicadores.error} reintentar={indicadores.recargar} />}
      {v === null && indicadores.error === null && <Cargando texto="Calculando indicadores…" />}
      {nada && <Vacio titulo="No hay indicadores de calidad que mostrar." />}
      {errorPeriodo === null && v !== null && ve(IND.calidad.actividad) && <ActividadLimpieza desde={desde} hasta={hasta} idCentro={idCentro} />}
      {errorPeriodo === null && v !== null && !nada && d.cargando && x === null && <Cargando texto="Calculando indicadores…" />}
      {v !== null && d.error !== null && <ErrorCaja error={d.error} reintentar={d.recargar} />}
      {x !== null && (
        <>
          {cifrasIncidencias.length > 0 && (
            <div className="panel">
              <div className="panel-cabecera"><h2>Incidencias</h2></div>
              <div className="panel-cuerpo">
                <Cifras etiqueta="Incidencias" cifras={cifrasIncidencias} />
              </div>
            </div>
          )}
          {cifrasNc.length > 0 && (
            <div className="panel">
              <div className="panel-cabecera"><h2>No conformidades</h2></div>
              <div className="panel-cuerpo">
                <Cifras etiqueta="Resumen" cifras={cifrasNc} />
              </div>
            </div>
          )}
          {ve(IND.calidad.ncPorUbicacion) && (
          <div className="panel">
            <div className="panel-cabecera"><h2>Dónde aparecen</h2></div>
            <div className="panel-cuerpo">
              <p className="pequena tenue" style={{ marginTop: 0 }}>No conformidades abiertas en el periodo, por ubicación de tus centros.</p>
              {x.porUbicacion.length === 0 ? <p className="pequena tenue">No hay no conformidades en este periodo.</p> : (
                <div className="tabla-desliza">
                  <table className="tabla-tarjetas kpi-tabla" aria-label="No conformidades por ubicación">
                    <thead><tr><th>Ubicación</th><th>Centro</th><th>No conformidades</th><th>Activas</th><th>Cerradas</th></tr></thead>
                    <tbody>
                      {x.porUbicacion.map((u) => (
                        <tr key={`${u.idCentro}-${u.idUbicacion ?? 'sin'}`}>
                          <td className="celda-titulo"><Link to={enlace(u)}>{u.ubicacion}</Link></td>
                          <td data-label="Centro">{u.centro}</td>
                          <td data-label="No conformidades" className="mono">{u.registradas}</td>
                          <td data-label="Activas">{u.activas}</td>
                          <td data-label="Cerradas">{u.cerradas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          )}
          {fila3.some(Boolean) && (
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'stretch' }}>
            {ve(IND.calidad.ncGravedad) && (
            <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
              <div className="panel-cabecera"><h2>Gravedad</h2></div>
              <div className="panel-cuerpo">
                <p className="pequena tenue" style={{ marginTop: 0 }}>De las abiertas en el periodo que el equipo de servicio ya ha clasificado.</p>
                <ul className="lista-kpi" aria-label="Gravedad">
                  {x.gravedad.map((g) => <li key={g.clave}><span>{g.etiqueta}</span><span className="mono">{g.total}</span></li>)}
                </ul>
              </div>
            </div>
            )}
            {ve(IND.calidad.ncPorCentro) && (
            <div className="panel" style={{ flex: '1 1 280px', margin: 0 }}>
              <div className="panel-cabecera"><h2>Por centro</h2></div>
              <div className="panel-cuerpo">
                {x.porCentro.length === 0 ? <p className="pequena tenue">No hay no conformidades en este periodo.</p> : (
                  <ul className="lista-kpi" aria-label="Por centro">
                    {x.porCentro.map((c) => (
                      <li key={c.idCentro}>
                        <span>{c.centro}</span>
                        <span className="pequena">{c.registradas} en el periodo · {c.activas} activa(s)</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            )}
          </div>
          )}
        </>
      )}
      {v !== null && errorPeriodo === null && (
        <BloqueEvolucion desde={desde} hasta={hasta} idCentro={idCentro} visibles={v}
          abierto={lista.valor('evolucion') === '1'} alCambiar={(a) => lista.fijar('evolucion', a ? '1' : '')} />
      )}
    </>
  );
}

export function TarjetaNcPublica({ n, volver }: { n: NcPublicaFila; volver?: { a: string; texto: string } }): ReactNode {
  return (
    <li className="nc-tarjeta">
      <Link to={`/portal/calidad/no-conformidades/${n.id}`} state={volver === undefined ? undefined : { volver }} className="nc-tarjeta-cabecera" style={{ textDecoration: 'none' }}>
        <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="mono">{n.codigo}</span>
          <EstadoNc estado={n.estado} />
          <NivelEtiqueta nivel={n.gravedad} />
          {n.novedadEmpresa === true && <span className="etiqueta info">Novedad del equipo de servicio</span>}
        </span>
        <span className="pequena suave">{fecha(n.fechaApertura)}</span>
      </Link>
      <div className="nc-tarjeta-resumen">
        <p>{n.titulo}</p>
        <p className="pequena tenue">
          {[n.centro.nombre, n.ubicacion ?? null, n.ot === null ? 'Del centro (sin OT)' : n.ot.codigo].filter((x) => x !== null && x !== '').join(' · ')}
        </p>
        <p className="pequena tenue">
          {n.abiertaPorTi ? 'Abierta por ti' : ETIQUETA_ORIGEN[n.origen] ?? n.origen}
          {n.ultimaActividad !== undefined && ` · Última actividad: ${fechaHora(n.ultimaActividad)}`}
          {n.fechaCierre !== null && ` · Cerrada el ${fecha(n.fechaCierre)}`}
        </p>
      </div>
    </li>
  );
}

// ------------------------------------------------------------------ alta (F11.3)

const IMPACTOS = [['BAJO', 'Bajo'], ['MEDIO', 'Medio'], ['ALTO', 'Alto']] as const;
const URGENCIAS = [['BAJA', 'Baja'], ['MEDIA', 'Media'], ['ALTA', 'Alta']] as const;

/** Opciones de radio grandes (44 px), con «Sin indicar» explícito: no es la gravedad oficial. */
function Percepcion({ legenda, nombre, valor, opciones, cambiar }: {
  legenda: string; nombre: string; valor: string; opciones: readonly (readonly [string, string])[]; cambiar: (v: string) => void;
}): ReactNode {
  return (
    <fieldset className="campo filtros-calidad" style={{ border: 0, padding: 0 }}>
      <legend className="etiqueta-dato">{legenda}</legend>
      <div className="lista-checks-horizontal">
        {[['', 'Sin indicar'] as const, ...opciones].map(([v, t]) => (
          <label key={v} className="fila" style={{ gap: 6 }}>
            <input type="radio" name={nombre} checked={valor === v} onChange={() => cambiar(v)} />
            {t}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * F11.3 — EL formulario de alta del cliente: desde Calidad (elige centro y,
 * si quiere, ubicación) o desde su OT (OT fija, líneas opcionales, centro
 * inferido). Una sola vía en el servidor. Nunca gravedad (la clasifica la
 * empresa), responsable, análisis ni visibilidad: su NC siempre la ve él.
 */
export function FormularioNcCliente({
  ot,
  lineaInicial = null,
  cancelar,
  alCrear,
}: {
  ot?: { id: string; codigo: string; centro: string; lineas: readonly { id: string; ruta: string }[] };
  lineaInicial?: string | null;
  cancelar?: () => void;
  alCrear: (idNc: string, codigo: string) => void;
}): ReactNode {
  const idBase = useId();
  const centros = useCentros();
  const [idCentro, setIdCentro] = useState('');
  const [idUbicacion, setIdUbicacion] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [impacto, setImpacto] = useState('');
  const [urgencia, setUrgencia] = useState('');
  const [lineas, setLineas] = useState<ReadonlySet<string>>(() => new Set(lineaInicial === null ? [] : [lineaInicial]));
  const [archivo, setArchivo] = useState<File | null>(null);
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ubicaciones = useDatos(
    () => (ot !== undefined || idCentro === '' ? Promise.resolve({ filas: [] as { id: string; nombre: string }[] })
      : api.get<{ filas: { id: string; nombre: string }[] }>(`/api/portal/calidad/centros/${idCentro}/ubicaciones`)),
    [idCentro, ot === undefined],
  );

  async function enviar(): Promise<void> {
    if (ot === undefined && idCentro === '') {
      setError(new globalThis.Error('Elige el centro.'));
      return;
    }
    if (descripcion.trim() === '') {
      setError(new globalThis.Error('Describe qué has detectado.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<ResultadoCrearNc>('/api/no-conformidades', {
        descripcion: descripcion.trim(), idOperacionCliente,
        ...(ot !== undefined ? { idOt: ot.id, ...(lineas.size > 0 ? { idsLineas: [...lineas] } : {}) } : { idCentro }),
        ...(ot === undefined && idUbicacion !== '' ? { idUbicacion } : {}),
        ...(impacto !== '' ? { impacto } : {}),
        ...(urgencia !== '' ? { urgencia } : {}),
      });
      //  La evidencia opcional se adjunta a la NC ya creada (misma ruta que «Añadir evidencia»).
      if (archivo !== null) {
        const fd = new FormData();
        fd.set('contexto', 'APERTURA_NC');
        fd.set('archivo', archivo, archivo.name);
        await api.subir(`/api/no-conformidades/${r.idNc}/evidencias`, fd);
      }
      alCrear(r.idNc, r.codigo);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea nc-formulario-cliente">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {ot !== undefined ? (
          <p className="pequena">Orden de trabajo <strong>{ot.codigo}</strong> · {ot.centro}</p>
        ) : (
          <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-c`}>Centro</label>
              <select id={`${idBase}-c`} value={idCentro} onChange={(e) => { setIdCentro(e.target.value); setIdUbicacion(''); }}>
                <option value="">Elige el centro…</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-u`}>Ubicación (opcional)</label>
              <select id={`${idBase}-u`} value={idUbicacion} disabled={idCentro === ''} onChange={(e) => setIdUbicacion(e.target.value)}>
                <option value="">Sin concretar</option>
                {(ubicaciones.datos?.filas ?? []).map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
              </select>
            </div>
          </div>
        )}
        <div className="campo">
          <label htmlFor={`${idBase}-d`}>¿Qué has detectado?</label>
          <textarea id={`${idBase}-d`} rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        {ot !== undefined && ot.lineas.length > 0 && (
          <fieldset className="campo filtros-calidad" style={{ border: 0, padding: 0 }}>
            <legend className="etiqueta-dato">Ubicaciones afectadas (opcional; ninguna = toda la OT)</legend>
            <div className="lista-checks">
              {ot.lineas.map((l) => (
                <label key={l.id} className="fila" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={lineas.has(l.id)}
                    onChange={() => setLineas((prev) => { const s = new Set(prev); if (s.has(l.id)) s.delete(l.id); else s.add(l.id); return s; })}
                  />
                  {l.ruta}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <Percepcion legenda="¿Cuánto te afecta? (opcional)" nombre={`${idBase}-imp`} valor={impacto} opciones={IMPACTOS} cambiar={setImpacto} />
        <Percepcion legenda="¿Con qué urgencia lo necesitas resuelto? (opcional)" nombre={`${idBase}-urg`} valor={urgencia} opciones={URGENCIAS} cambiar={setUrgencia} />
        <p className="pequena suave" style={{ marginTop: 0 }}>La gravedad oficial la clasifica el equipo de servicio; lo que indiques le sirve de contexto.</p>
        <div className="campo">
          <label htmlFor={`${idBase}-f`}>Foto o documento (opcional)</label>
          <input id={`${idBase}-f`} type="file" accept="image/*,application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
        </div>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
            {enviando ? 'Enviando…' : 'Abrir no conformidad'}
          </button>
          {cancelar !== undefined && <button type="button" className="pequeno" disabled={enviando} onClick={cancelar}>Cancelar</button>}
        </div>
      </div>
    </div>
  );
}

export function PaginaPortalNuevaNc(): ReactNode {
  const navegar = useNavigate();
  const volver = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? { a: '/portal/calidad?tab=nc', texto: 'Calidad' };
  return (
    <Shell titulo="Nueva no conformidad" migas={[volver, { texto: 'Nueva no conformidad' }]}>
      <FormularioNcCliente
        cancelar={() => navegar(volver.a)}
        alCrear={(idNc) => navegar(`/portal/calidad/no-conformidades/${idNc}`, { replace: true, state: { volver, recienCreada: true } })}
      />
    </Shell>
  );
}

// ------------------------------------------------------------------ detalles

export function PaginaPortalIncidencia(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const d = useDatos(() => api.get<IncidenciaPublicaDetalle>(`/api/portal/calidad/incidencias/${id ?? ''}`), [id]);
  const migas = [{ texto: 'Calidad', a: '/portal/calidad' }, { texto: d.datos?.codigo ?? 'Incidencia' }];
  if (d.cargando && d.datos === null) return <Shell titulo="Incidencia" migas={migas}><Cargando /></Shell>;
  if (d.error !== null || d.datos === null) {
    return <Shell titulo="Incidencia" migas={migas}><ErrorCaja error={d.error ?? new globalThis.Error('Esa incidencia no existe.')} /></Shell>;
  }
  const inc = d.datos;
  return (
    <Shell
      titulo={`Incidencia ${inc.codigo}`}
      migas={migas}
      subtitulo={<span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}><EstadoIncidenciaEtiqueta estado={inc.estado} /><NivelEtiqueta nivel={inc.nivel} /></span>}
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <p>{inc.descripcion}</p>
          <div className="datos">
            <Dato e="Centro">{inc.centro.nombre}</Dato>
            <Dato e="Orden de trabajo"><ContextoOt ot={inc.ot} /></Dato>
            {/* Portal v2: la tarea de la OT, si la hay; si no, la ubicación que se reportó (fotografía, 0098). */}
            {inc.linea !== null
              ? <Dato e="Ubicación">{inc.linea}</Dato>
              : inc.ubicacion !== undefined && inc.ubicacion !== null && <Dato e="Ubicación">{inc.ubicacion}</Dato>}
            <Dato e="Registrada">{fechaHora(inc.creadaEn)}</Dato>
            {inc.resueltaEn !== null && <Dato e="Resuelta">{fechaHora(inc.resueltaEn)}</Dato>}
            {inc.resolucion !== null && (
              <Dato e="Resolución">
                {inc.nc !== null && inc.resolucion.startsWith('Resuelta mediante')
                  ? <>Resuelta mediante <Link to={`/portal/calidad/no-conformidades/${inc.nc.id}`}>{inc.nc.codigo}</Link></>
                  : inc.resolucion}
              </Dato>
            )}
            {inc.nc !== null && (
              <Dato e="No conformidad"><Link to={`/portal/calidad/no-conformidades/${inc.nc.id}`}>{inc.nc.codigo}</Link></Dato>
            )}
          </div>
        </div>
      </div>
      <div className="panel">
        <div className="panel-cabecera"><h2>Evidencias</h2></div>
        <div className="panel-cuerpo"><ListaEvidenciasPublicas evidencias={inc.evidencias} /></div>
      </div>
      <div className="panel">
        <div className="panel-cabecera"><h2>Evolución</h2></div>
        <div className="panel-cuerpo"><Evolucion hitos={inc.evolucion} /></div>
      </div>
    </Shell>
  );
}

export function PaginaPortalNc(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const estadoNav = useLocation().state as { volver?: { a: string; texto: string }; recienCreada?: boolean } | null;
  const volver = estadoNav?.volver ?? { a: '/portal/calidad?tab=nc', texto: 'Calidad' };
  const d = useDatos(() => api.get<NcPublicaDetalle>(`/api/portal/calidad/no-conformidades/${id ?? ''}`), [id]);
  const [mensaje, setMensaje] = useState<string | null>(estadoNav?.recienCreada === true ? 'No conformidad abierta. El equipo de servicio ya la ve.' : null);
  const migas = [volver, { texto: d.datos?.codigo ?? 'No conformidad' }];
  if (d.cargando && d.datos === null) return <Shell titulo="No conformidad" migas={migas}><Cargando /></Shell>;
  if (d.error !== null || d.datos === null) {
    return <Shell titulo="No conformidad" migas={migas}><ErrorCaja error={d.error ?? new globalThis.Error('Esa no conformidad no existe.')} /></Shell>;
  }
  const nc = d.datos;
  const anulada = nc.estado === 'ANULADA';
  const percepcion = textoPercepcionCliente(nc.impactoCliente, nc.urgenciaCliente);
  function tras(texto: string): void {
    setMensaje(texto);
    d.recargar();
  }
  return (
    <Shell
      titulo={`No conformidad ${nc.codigo}`}
      migas={migas}
      subtitulo={<span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}><EstadoNc estado={nc.estado} /><NivelEtiqueta nivel={nc.gravedad} /></span>}
    >
      {mensaje !== null && <div className="nota exito" role="status">{mensaje}</div>}
      <div className="panel nc-seccion">
        <div className="panel-cuerpo">
          <p style={{ whiteSpace: 'pre-wrap' }}>{nc.descripcion}</p>
          <div className="datos">
            <Dato e="Centro">{nc.centro.nombre}</Dato>
            {nc.ubicacion != null && <Dato e="Ubicación">{nc.ubicacion}</Dato>}
            <Dato e="Orden de trabajo">{nc.ot === null ? <span>Del centro (sin OT)</span> : <ContextoOt ot={nc.ot} />}</Dato>
            <Dato e="Origen">{nc.abiertaPorTi ? 'Abierta por ti' : ETIQUETA_ORIGEN[nc.origen] ?? nc.origen}</Dato>
            <Dato e="Apertura">{fecha(nc.fechaApertura)}</Dato>
            <Dato e="Gravedad oficial">{nc.gravedad === null ? 'Pendiente de clasificar por el equipo de servicio' : <NivelEtiqueta nivel={nc.gravedad} />}</Dato>
            {percepcion !== null && <Dato e="Lo que indicasteis">{percepcion}</Dato>}
            {nc.fechaCierre !== null && (
              <Dato e="Cierre">{fecha(nc.fechaCierre)}{nc.cerradaPorCliente === true ? ' · la cerrasteis vosotros' : ''}</Dato>
            )}
            {nc.resolucion !== null && <Dato e="Resolución">{nc.resolucion}</Dato>}
          </div>
          {nc.estado === 'CERRADA' && (
            <ReabrirNc idNc={nc.id} permiso="nc:reabrir_propia" alReabrir={() => tras('Has reabierto la no conformidad. El equipo de servicio la retomará.')} />
          )}
          {(nc.estado === 'ABIERTA' || nc.estado === 'EN_TRATAMIENTO') && (
            <CerrarNcCliente idNc={nc.id} actuacionesEnCurso={nc.actuacionesEnCurso === true} alCerrar={() => tras('Has cerrado la no conformidad. Si vuelve a ocurrir, puedes reabrirla.')} />
          )}
          {nc.lineas.length > 0 && (
            <div className="sep">
              <div className="etiqueta-dato">Ubicaciones afectadas</div>
              <ul className="lista-nc-lineas">{nc.lineas.map((l) => <li key={l} className="pequena">{l}</li>)}</ul>
            </div>
          )}
          {nc.incidencias.length > 0 && (
            <div className="sep">
              <div className="etiqueta-dato">Incidencias relacionadas</div>
              <ul className="lista-nc-lineas">
                {nc.incidencias.map((i) => (
                  <li key={i.id}>
                    <Link to={`/portal/calidad/incidencias/${i.id}`} className="mono pequena">{i.codigo}</Link>{' '}
                    <EstadoIncidenciaEtiqueta estado={i.estado} /> <span className="pequena tenue">{i.titulo}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <ConversacionPublica idNc={nc.id} comentarios={nc.comentarios ?? []} anulada={anulada} alComentar={() => tras('Comentario enviado.')} />
      <div className="panel nc-seccion">
        <div className="panel-cabecera">
          <h2>Evidencias</h2>
        </div>
        <div className="panel-cuerpo">
          {!anulada && <AnadirEvidenciaPublica idNc={nc.id} estado={nc.estado} alSubir={() => tras('Evidencia añadida.')} />}
          <ListaEvidenciasPublicas evidencias={nc.evidencias} />
        </div>
      </div>
      <div className="panel nc-seccion">
        <div className="panel-cabecera"><h2>Acciones correctivas</h2></div>
        <div className="panel-cuerpo"><AccionesPublicas acciones={nc.acciones ?? []} /></div>
      </div>
      <div className="panel nc-seccion">
        <div className="panel-cabecera"><h2>Evolución</h2></div>
        <div className="panel-cuerpo"><Evolucion hitos={nc.evolucion} /></div>
      </div>
    </Shell>
  );
}

/**
 * F11.3 — el cliente cierra su NC cuando considera resuelto el asunto: sólo un motivo. Mientras quede alguna
 * actuación en curso (también interna), el botón se ve deshabilitado con una explicación neutra — sin número,
 * responsable ni plazo. La regla la aplica el servidor de todos modos (NC_ACCIONES_ABIERTAS).
 */
function CerrarNcCliente({ idNc, actuacionesEnCurso, alCerrar }: { idNc: string; actuacionesEnCurso: boolean; alCerrar: () => void }): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [idOperacionCliente] = useState(() => crypto.randomUUID());
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!puede('nc:cerrar_propia')) return null;

  async function enviar(): Promise<void> {
    if (motivo.trim() === '') {
      setError(new globalThis.Error('Explica por qué la das por resuelta.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/no-conformidades/${idNc}/cerrar`, { motivo: motivo.trim(), idOperacionCliente });
      setAbierto(false);
      setMotivo('');
      alCerrar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (actuacionesEnCurso) {
    return (
      <div className="sep" role="region" aria-label="Cerrar">
        <button type="button" className="pequeno" disabled aria-describedby={`${idBase}-n`}>Cerrar no conformidad</button>
        <p id={`${idBase}-n`} className="pequena suave" style={{ marginBottom: 0 }}>
          Esta no conformidad todavía tiene actuaciones en curso. Podrás cerrarla cuando finalicen.
        </p>
      </div>
    );
  }
  if (!abierto) {
    return (
      <div className="lista-checks-horizontal" style={{ marginTop: 10 }}>
        <button type="button" className="pequeno" onClick={() => setAbierto(true)}>Cerrar no conformidad</button>
      </div>
    );
  }
  return (
    <div className="panel accion-linea" role="region" aria-label="Cerrar">
      <div className="panel-cuerpo">
        <h4 style={{ margin: '0 0 4px' }}>Cerrar la no conformidad</h4>
        <p className="pequena suave" style={{ marginTop: 0 }}>
          Al cerrarla indicas que, para vosotros, el asunto está resuelto. El equipo de servicio verá tu motivo y, si
          vuelve a ocurrir, podrás reabrirla.
        </p>
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-m`}>Motivo del cierre</label>
          <textarea id={`${idBase}-m`} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>{enviando ? 'Cerrando…' : 'Confirmar cierre'}</button>
          <button type="button" className="pequeno" disabled={enviando} onClick={() => { setAbierto(false); setError(null); }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/** F11.3 — la conversación visible. Lo que el cliente escribe es siempre visible, no se edita ni se borra. */
function ConversacionPublica({ idNc, comentarios, anulada, alComentar }: {
  idNc: string; comentarios: readonly ComentarioPublico[]; anulada: boolean; alComentar: () => void;
}): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (texto.trim() === '') {
      setError(new globalThis.Error('Escribe el comentario.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/no-conformidades/${idNc}/comentarios`, { texto: texto.trim() });
      setTexto('');
      alComentar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel nc-seccion">
      <div className="panel-cabecera"><h2>Conversación</h2></div>
      <div className="panel-cuerpo">
        {comentarios.length === 0 ? (
          <p className="pequena tenue">Todavía no hay comentarios.</p>
        ) : (
          <ul className="lista-nc-historial" aria-label="Comentarios">
            {comentarios.map((k) => (
              <li key={k.id} className={k.autor.tipo === 'CLIENTE' ? 'comentario-cliente' : undefined}>
                <span className="pequena"><strong>{k.esTuyo ? 'Tú' : k.autor.nombre}</strong></span>
                <span className="pequena tenue"> · {fechaHora(k.creadoEn)}</span>
                <p className="pequena" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{k.texto}</p>
              </li>
            ))}
          </ul>
        )}
        {!anulada && puede('nc:comentar') && (
          <div className="sep">
            {error !== null && <ErrorCaja error={error} />}
            <div className="campo">
              <label htmlFor={`${idBase}-t`}>Escribe un comentario</label>
              <textarea id={`${idBase}-t`} rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} />
            </div>
            <p className="pequena suave" style={{ marginTop: 0 }}>Lo verá el equipo de servicio. Una vez enviado no se puede editar.</p>
            <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>{enviando ? 'Enviando…' : 'Enviar comentario'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

/** F11.3 — «Añadir evidencia» en cualquier NC visible: siempre compartida (la impone el servidor). */
function AnadirEvidenciaPublica({ idNc, estado, alSubir }: { idNc: string; estado: string; alSubir: () => void }): ReactNode {
  const idBase = useId();
  const [abierto, setAbierto] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (archivo === null) {
      setError(new globalThis.Error('Elige una foto o un documento.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.set('contexto', estado === 'CERRADA' ? 'RESOLUCION_NC' : estado === 'EN_TRATAMIENTO' ? 'TRATAMIENTO_NC' : 'APERTURA_NC');
      if (descripcion.trim() !== '') fd.set('descripcion', descripcion.trim());
      fd.set('archivo', archivo, archivo.name);
      await api.subir(`/api/no-conformidades/${idNc}/evidencias`, fd);
      setAbierto(false);
      setArchivo(null);
      setDescripcion('');
      alSubir();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (!abierto) {
    return (
      <div className="lista-checks-horizontal" style={{ marginBottom: 10 }}>
        <button type="button" className="pequeno" onClick={() => setAbierto(true)}>Añadir evidencia</button>
      </div>
    );
  }
  return (
    <div className="panel accion-linea">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-f`}>Foto o documento</label>
          <input id={`${idBase}-f`} type="file" accept="image/*,application/pdf" onChange={(e) => setArchivo(e.target.files?.[0] ?? null)} />
        </div>
        <div className="campo">
          <label htmlFor={`${idBase}-d`}>Descripción (opcional)</label>
          <textarea id={`${idBase}-d`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>{enviando ? 'Subiendo…' : 'Subir evidencia'}</button>
          <button type="button" className="pequeno" disabled={enviando} onClick={() => setAbierto(false)}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/** F11.3 — acciones correctivas COMPARTIDAS: sólo lectura, sin responsable ni plazo. */
function AccionesPublicas({ acciones }: { acciones: readonly AccionPublica[] }): ReactNode {
  if (acciones.length === 0) return <p className="pequena tenue">Todavía no hay acciones correctivas compartidas.</p>;
  return (
    <ul className="lista-nc">
      {acciones.map((a) => (
        <li key={a.id} className="nc-tarjeta">
          <div className="nc-tarjeta-resumen" style={{ paddingTop: 12 }}>
            <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
              <EstadoAccionEtiqueta estado={a.estado} />
              {a.completadaEn !== null && <span className="pequena tenue">Completada el {fechaHora(a.completadaEn)}</span>}
            </div>
            <p>{a.descripcion}</p>
            {a.resultado !== null && <p className="pequena suave">Resultado: {a.resultado}</p>}
            {a.evidencias.length > 0 && <ListaEvidenciasPublicas evidencias={a.evidencias} />}
          </div>
        </li>
      ))}
    </ul>
  );
}
